import { BiddingUtils } from '@/vb-utils/bidding.util';
import { SOCIAL_LINKS } from '@/vb-utils/socials-handler';
import { Component } from '@angular/core';
import { ContactUsFormValue, RoleOption, SubjectOption } from '@viewbid/ui';
import {
    NotificationsService,
    ContactRole,
    Subject,
    CreateContactUsRequest,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { TranslateService } from '@ngx-translate/core';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';

const ROLE_MAP: Record<string, ContactRole> = {
    bidder: ContactRole.Bidder,
    seller: ContactRole.Seller,
    house: ContactRole.AuctionHouseRepresentative,
};

const SUBJECT_MAP: Record<string, Subject> = {
    'General inquiry': Subject.GeneralInquiry,
    'Bidding help': Subject.BiddingQuestions,
    'Selling or consignment': Subject.Other,
    'Payments & invoices': Subject.PaymentIssues,
    'Auction house partnership': Subject.Other,
    'Report a problem': Subject.TechnicalSupport,
};

@Component({
    selector: 'vb-contact-us-page',
    templateUrl: './contact-us-page.component.html',
    styleUrls: ['./contact-us-page.component.scss'],
})
export class ContactUsPageComponent {
    SOCIAL_LINKS = SOCIAL_LINKS;
    currentUserId: string = '';
    /** Bound to vb-contact-us's [sent] — the library no longer decides this
     *  for itself, so it's only set true once createContactUs actually
     *  succeeds (never on failure, and never before the request resolves). */
    sent: boolean = false;

    /** The auction-house options aren't taking inquiries yet, so they carry
     *  `enabled: false` — the form still lists them, greyed out and
     *  unselectable, rather than hiding that they exist. */
    readonly roles: RoleOption[] = [
        { id: 'bidder', label: 'Bidder', desc: 'I bid on lots' },
        { id: 'seller', label: 'Seller', desc: 'I sell lots', enabled: false },
    ];

    readonly subjects: SubjectOption[] = [
        { value: 'General inquiry' },
        { value: 'Bidding help' },
        { value: 'Selling or consignment' },
        { value: 'Payments & invoices' },
        { value: 'Auction house partnership', enabled: false },
        { value: 'Report a problem' },
    ];

    constructor(
        private biddingUtils: BiddingUtils,
        private notificationsService: NotificationsService,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private recaptchaV3Service: ReCaptchaV3Service,
    ) {
        this.biddingUtils
            .getCurrentUserId()
            .subscribe((currentUserId: string) => {
                this.currentUserId = currentUserId;
            });
    }

    getSocialKeys(): string[] {
        return Object.keys(this.SOCIAL_LINKS);
    }

    onSubmitted(value: ContactUsFormValue): void {
        this.recaptchaV3Service.execute('submit').subscribe({
            next: (captchaToken: string) => {
                const role: ContactRole | null = value.role
                    ? ROLE_MAP[value.role]
                    : null;
                if (!role) {
                    this.showInquiryFailedToast();
                    return;
                }

                const request: CreateContactUsRequest = {
                    firstname: value.firstName,
                    lastname: value.lastName,
                    email: value.email,
                    role,
                    subject: SUBJECT_MAP[value.subject] ?? Subject.Other,
                    message: value.message,
                    reCaptchaToken: captchaToken,
                    subscribe: false,
                    promotionalConsent: false,
                    ...(value.phone?.trim() && { telephone: value.phone }),
                };

                this.notificationsService.createContactUs(request).subscribe({
                    next: () => {
                        this.sent = true;
                        this.snackbarService.success(
                            this.translate.instant(
                                'ContactUs.InquiryAddedSuccessfully',
                            ),
                        );
                    },
                    error: () => this.showInquiryFailedToast(),
                });
            },
            // The recaptcha round trip can fail on its own (script blocked,
            // network hiccup) before createContactUs is ever called — that
            // failure needs the same toast, not silence.
            error: () => this.showInquiryFailedToast(),
        });
    }

    private showInquiryFailedToast(): void {
        this.snackbarService.error(
            this.translate.instant('ContactUs.AddInquiryFailed'),
        );
    }

    onSentChange(sent: boolean): void {
        this.sent = sent;
    }
}
